import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import 'rxjs/add/observable/of';
import { switchMap, first } from "rxjs/operators";

//Firebase
import { AngularFireAuth } from "angularfire2/auth";
import { AngularFirestore, AngularFirestoreDocument } from "angularfire2/firestore";
import * as firebase from "firebase/app"

//Variables
import { environment } from '@env/environment';
import { StorageService } from '@app/core/services/storage.service';
import { MessagingService } from './messaging.service';

export interface User{
  uid: string,
  email: string,
  displayName: string,
  photoURL: string,
  provider: string,
  lastLogin: firebase.firestore.Timestamp,
  createdAt: firebase.firestore.Timestamp
}

@Injectable()
export class AuthService {
  //isLoggedIn: boolean = false;

  user: Observable<any>;

  // store the URL so we can redirect after logging in
  redirectUrl: string;

  // UID
  uid: string;

  constructor(private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private storage: StorageService,
    private fcm: MessagingService){

      this.user = this.afAuth.authState.pipe(
        switchMap(user => {
          if (user) {
            return this.afs.doc<any>(`${environment.users}/${user.uid}`).valueChanges();
          } else {
            return Observable.of(null);
          }
        })
      );
  }

  //Google
  async webGoogleLogin(): Promise<void> {
    try{
      const provider = new firebase.auth.GoogleAuthProvider();
      const credential = await this.afAuth.auth.signInWithPopup(provider);
            
      let user: any = JSON.parse(JSON.stringify(credential.user));

      return await this.updateUserData(user, 'google');
    } catch(err) {
      console.error("web error", err);
      return err;
    }
  }

  //Twwiter
  async webTwitterLogin(): Promise<void> {
    try {
      const provider = new firebase.auth.TwitterAuthProvider();
      const credential = await this.afAuth.auth.signInWithPopup(provider);

      return await this.updateUserData(credential.user, 'twitter');
    } catch (err) {
      console.error(err);
      return err;
    }
  }

  //Facebook
  async webFacebookLogin(): Promise<void> {
    try {
      const provider = new firebase.auth.FacebookAuthProvider();
      const credential = await this.afAuth.auth.signInWithPopup(provider);

      let user: any = JSON.parse(JSON.stringify(credential.user));

      return await this.updateUserData(user, 'facebook');
    } catch (err) {
      console.error(err);
      return err;
    }
  }

  //Email & Password //
  async login(email: string, password: string): Promise<void> {
    try{
      
      const emailCredential = firebase.auth.EmailAuthProvider.credential(email, password);
      
      const firebaseUser = await firebase
        .auth()
        .signInAndRetrieveDataWithCredential(emailCredential);


      let user: any = JSON.parse(JSON.stringify(firebaseUser.user));
      
      return await this.updateUserData(user, 'email');
    } catch(err) {
      console.error('Error al autenticar', err);
      return err;
    }
  }


  async resetPassword(email: string): Promise<void> {
    try{
      return this.afAuth.auth.sendPasswordResetEmail(email);
    } catch(err) {
      console.error("Error al resetear password", err);
      return err;
    }   
  }

  async createUser(newName: string, newEmail: string, newPassword: string): Promise<any> {
    try{
      await this.afAuth.auth.createUserWithEmailAndPassword(newEmail, newPassword);

      const user = await this.afAuth.auth.currentUser;
      return await user.updateProfile({
          displayName: newName,
          photoURL: 'https://goo.gl/7kz9qG'
        });
    } catch(err) {      
      console.error("Error al crear usuario", err);
      return err;
    }
  } 

  userExists(path: string) {
    return this.afs.doc(path).valueChanges().pipe(first()).toPromise()
  }

  // Guardar datos en Firestore
  async updateUserData(user: any, provider: any) {
    const doc = await this.userExists(`${environment.users}/${user.uid}`);

    if (!doc) {
      throw {error_code:999, error_message: 'Acceso denegado, no tiene permisos para acceder a este servicio.'};
    } 

    const userRef: AngularFirestoreDocument<any> = this.afs.doc(
      `${  environment.users}/${user.uid}`
    );

    this.storage.setItem("uid", user.uid);
    this.storage.setItem("name", user.displayName);

    console.log(user, Number(user.lastLoginAt)*1000, new Date(Number(user.lastLoginAt)*1000));

    const data = {
      uid: user.uid,
      email: user.email || null,
      displayName: user.displayName,
      photoURL: user.photoURL || "https://goo.gl/7kz9qG",
      provider: provider,
      lastLogin: new Date(Number(user.lastLoginAt)) || null,
      createdAt: new Date(Number(user.createdAt)) || null
    };

    console.log(data);

    this.fcm.getPermission(user);
    this.fcm.monitorRefresh(user);
    this.fcm.receiveMessages();

    return userRef.set(data, { merge: true });
  }


  async logout(): Promise<any> {
    localStorage.clear();
    this.afAuth.auth.signOut();
  }

  // Current user as a Promise. Useful for one-off operations.
  async getCurrentUser(): Promise<any> {
    return this.user.pipe(first()).toPromise();
  }

  // Current user as boolean Promise. Used in router guards
  async isLoggedIn(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return !!user;
  }

  public getOfficesAuth(user: any){
    let uid = this.storage.getItem("uid");
    const filtro = `users.${uid}`;
    return this.afs.collection("offices", ref => ref.where(filtro, "==", true))
    //return this.afs.collection("offices");
  }

  public getShopsAuth(user: any){
    let uid = this.storage.getItem("uid");
    const filtro = `users.${uid}`;
    return this.afs.collection("shops", ref => ref.where(filtro, "==", true))
    //return this.afs.collection("offices");
  }

  setOfficeSelected(office: any){
    this.storage.setItem("office", JSON.stringify(office));
  }

  setOfficeBase(office: any){
    this.storage.setItem("officebase", JSON.stringify(office));
  }

  getOfficeSelected(){
    return JSON.parse(this.storage.getItem("office"));
  }

  getOfficeBase(){
    return JSON.parse(this.storage.getItem("officebase"));
  }

  public getUser(){
    return {uid: this.storage.getItem("uid"), displayName: this.storage.getItem("name")}
  }
}
